import React, { useState } from "react";
import { EditorState, convertToRaw } from "draft-js";
import { Editor as WysiwygEditor } from 'react-draft-wysiwyg';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "draft-js/dist/Draft.css"; 

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function AddEventForm({ onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    category: "",
    start_date: "",
    end_date: "",
    body: EditorState.createEmpty(),  // Initialize Draft.js editor state
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Handles form changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handles Draft.js editor changes
  const handleEditorChange = (editorState) => {
    setForm({ ...form, body: editorState });
  };

  // Handles file uploads (thumbnail and images)
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setThumbnail(file);
    } else {
      setError("Please select a valid image file for thumbnail.");
      setThumbnail(null);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files).filter((file) =>
      file.type.startsWith("image/")
    );
    if (files.length > 0) {
      setImages(files);
    } else {
      setError("Please select valid image files.");
      setImages([]);
    }
  };

  // Validate video URL
  const isValidVideoUrl = (url) => {
    const videoPatterns = [
      /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/,
      /^https?:\/\/(www\.)?instagram\.com\/.+/,
      /^https?:\/\/(www\.)?facebook\.com\/.+/
    ];
    return videoPatterns.some(pattern => pattern.test(url));
  };

  // Add video URL to the list
  const handleAddVideo = () => {
    if (!videoUrl.trim()) {
      setError("Please enter a video URL.");
      return;
    }
    if (!isValidVideoUrl(videoUrl)) {
      setError("Please enter a valid YouTube, Instagram, or Facebook video URL.");
      return;
    }
    setVideos([...videos, videoUrl.trim()]);
    setVideoUrl("");
    setError("");
  };

  // Remove video from the list
  const handleRemoveVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  // Handles form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!localStorage.getItem("access_token")) {
      setError("You must be logged in to add an event.");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      const formatDateTime = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toISOString();
      };
      formData.append("name", form.name);
      formData.append("category", form.category);
      formData.append("start_date", formatDateTime(form.start_date));
      formData.append("end_date", formatDateTime(form.end_date));
      if (thumbnail) formData.append("thumbnail", thumbnail);
      images.forEach((img) => formData.append("image_files", img));
      
      // Add video URLs
      if (videos.length > 0) {
        formData.append("video_urls", JSON.stringify(videos));
      }

      // Append body content from Draft.js editor
      const contentState = form.body.getCurrentContent();
      const body = JSON.stringify(convertToRaw(contentState)); // Convert editor content to raw JSON for storage
      if (body.trim() !== "") {
        formData.append("body", body);
      } else {
        setError("Event body cannot be empty.");
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/events/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(JSON.stringify(errorData) || "Failed to add event");
      }
      setForm({ name: "", category: "", start_date: "", end_date: "", body: EditorState.createEmpty() });
      setThumbnail(null);
      setImages([]);
      setVideos([]);
      setVideoUrl("");
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Function to apply formatting to the selected text
  // const toggleInlineStyle = (style) => {
  //   const newState = RichUtils.toggleInlineStyle(form.body, style);
  //   handleEditorChange(newState);
  // };

  // Function to apply block-level styles like headings, blockquote, etc.
  // const toggleBlockType = (blockType) => {
  //   const newState = RichUtils.toggleBlockType(form.body, blockType);
  //   handleEditorChange(newState);
  // };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 600, margin: "auto", padding: 16 }}>
      <h2>Add Event</h2>
      <input
        name="name"
        type="text"
        placeholder="Event Name"
        value={form.name}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="category"
        type="text"
        placeholder="Category"
        value={form.category}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="start_date"
        type="datetime-local"
        value={form.start_date}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="end_date"
        type="datetime-local"
        value={form.end_date}
        onChange={handleChange}
        className="input"
      />
      <div style={{ marginTop: 8 }}>
        <label>
          Thumbnail:{" "}
          <input type="file" accept="image/*" onChange={handleThumbnailChange} />
        </label>
      </div>
      <div style={{ marginTop: 8 }}>
        <label>
          Images:{" "}
          <input type="file" accept="image/*" multiple onChange={handleImagesChange} />
        </label>
      </div>
      <div style={{ marginTop: 8 }}>
        <label>Videos (YouTube, Instagram, Facebook URLs):</label>
        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
          <input
            type="url"
            placeholder="Enter video URL (YouTube, Instagram, Facebook)"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <button type="button" onClick={handleAddVideo} className="btn-primary-primary">
            Add Video
          </button>
        </div>
        {videos.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <label>Added Videos:</label>
            <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
              {videos.map((videoUrl, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                  <span style={{ fontSize: '14px', flex: 1, wordBreak: 'break-all' }}>
                    {videoUrl.length > 50 ? `${videoUrl.substring(0, 50)}...` : videoUrl}
                  </span>
                  <button type="button" onClick={() => handleRemoveVideo(index)} style={{ background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '12px' }}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 8 }}>
        <label>Body:</label>
        {/* Draft.js Editor for event body */}
        <div style={{ border: "1px solid #ccc", padding: 8, marginTop: 8, height: "300px", overflowY: "auto" }}>
          <WysiwygEditor
            editorState={form.body}
            onEditorStateChange={handleEditorChange}
            toolbar={{
              options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
              inline: { inDropdown: true, options: ['bold', 'italic', 'underline', 'monospace'] },
              blockType: { inDropdown: true, options: ['Normal', 'H1', 'H2', 'H3', 'Blockquote'] },
              list: { inDropdown: true, options: ['unordered', 'ordered'] },
              textAlign: { inDropdown: true, options: ['left', 'center', 'right'] },
              link: { inDropdown: true },
              image: { inDropdown: true }
            }}
          />
        </div>
      </div>
      {error && <div style={{ color: "red", marginTop: 12 }}>{error}</div>}
      <button
        type="submit"
        className="btn-primary-primary"
        disabled={loading}
        style={{ marginTop: 12 }}
      >
        {loading ? "Saving..." : "Add Event"}
      </button>
    </form>
  );
}

export default AddEventForm;
